import { dancerImportRowSchema, type DancerInput } from '../schemas/dancer';
import type { ImportError, ImportSummary } from './errors';
import { parseCsv } from './parseCsv';

export const DANCER_REQUIRED_HEADERS = ['family_email', 'first_name', 'last_name', 'dob'] as const;
export const DANCER_OPTIONAL_HEADERS = ['preferred_name', 'pronouns', 'status'] as const;

export interface DancerImportResult {
  summary: ImportSummary;
  // Each row carries the resolved family_id once reconciled against the DB.
  pending: {
    rowNum: number;
    familyEmail: string;
    dancer: Omit<DancerInput, 'family_id'>;
  }[];
}

export function validateDancerCsv(csvText: string): DancerImportResult {
  const { headers, rows } = parseCsv(csvText);
  const errors: ImportError[] = [];
  const warnings: ImportError[] = [];
  const pending: DancerImportResult['pending'] = [];

  // Header validation.
  const headerSet = new Set(headers);
  for (const h of DANCER_REQUIRED_HEADERS) {
    if (!headerSet.has(h)) {
      errors.push({
        row: 0,
        column: h,
        kind: 'header_missing',
        message: `Required column "${h}" is missing from the file.`,
      });
    }
  }
  for (const h of headers) {
    if (
      !DANCER_REQUIRED_HEADERS.includes(h as never) &&
      !DANCER_OPTIONAL_HEADERS.includes(h as never)
    ) {
      warnings.push({
        row: 0,
        column: h,
        kind: 'header_unknown',
        message: `Column "${h}" is not recognized and will be ignored.`,
      });
    }
  }

  if (errors.length > 0) {
    return {
      summary: {
        totalRows: rows.length,
        validRows: 0,
        invalidRows: rows.length,
        errors,
        warnings,
      },
      pending: [],
    };
  }

  // Within-file duplicate detection: same family_email + first_name + dob.
  const seen = new Map<string, number>();

  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    const parsed = dancerImportRowSchema.safeParse(row);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const column = issue.path.join('.') || '(row)';
        errors.push({
          row: rowNum,
          column,
          kind: 'row_invalid',
          message: issue.message,
          value: typeof row[column] === 'string' ? row[column] : undefined,
        });
      }
      return;
    }

    const dupKey = `${parsed.data.family_email}|${parsed.data.first_name.toLowerCase()}|${parsed.data.dob}`;
    const prior = seen.get(dupKey);
    if (prior !== undefined) {
      errors.push({
        row: rowNum,
        kind: 'duplicate_in_file',
        message: `Same dancer (name + dob) already appears in this file at row ${prior}.`,
      });
      return;
    }
    seen.set(dupKey, rowNum);

    pending.push({
      rowNum,
      familyEmail: parsed.data.family_email,
      dancer: {
        first_name: parsed.data.first_name,
        last_name: parsed.data.last_name,
        preferred_name: parsed.data.preferred_name || undefined,
        date_of_birth: parsed.data.dob,
        pronouns: parsed.data.pronouns || undefined,
        status: parsed.data.status,
        standalone_login_consent: false,
      },
    });
  });

  return {
    summary: {
      totalRows: rows.length,
      validRows: pending.length,
      invalidRows: rows.length - pending.length,
      errors,
      warnings,
    },
    pending,
  };
}

// Resolve family_email → family_id by looking up against existing families.
// Returns rows ready to insert plus a fresh error list for unmatched families.
export function reconcileDancerImport(
  result: DancerImportResult,
  familyIdByEmail: Map<string, string>,
): { summary: ImportSummary; rows: DancerInput[] } {
  const errors = [...result.summary.errors];
  const rows: DancerInput[] = [];

  for (const item of result.pending) {
    const familyId = familyIdByEmail.get(item.familyEmail.toLowerCase());
    if (!familyId) {
      errors.push({
        row: item.rowNum,
        column: 'family_email',
        kind: 'foreign_key_missing',
        message: `No family found with email "${item.familyEmail}". Import the family first.`,
        value: item.familyEmail,
      });
      continue;
    }
    rows.push({ ...item.dancer, family_id: familyId });
  }

  return {
    summary: {
      ...result.summary,
      validRows: rows.length,
      invalidRows: result.summary.totalRows - rows.length,
      errors,
    },
    rows,
  };
}
